/**
 * Box 7, คำแนะนำย้ายงบ (spec §5.6, plan R27), through D1's OverviewCard: one line per adviceOf item, in the prototype's
 * moveAdvice words, with mROAS rounded down as box 4 prints it (T12h), so the two boxes never differ by 0.01.
 * - move: "ย้าย … จาก <from> ไป <to>" when auto gives `to` MIN_MOVE or more, otherwise "ลดงบ <from>", still naming the
 *   pair whose ranges part; then where auto sends the money (its increases, T9c), and the gain of the whole auto
 *   reallocation with its range and % of today;
 * - notYet in spec §5.6's words; stopped since the Sunday of its last active week (a Monday label, R1);
 * - quality: spec §5.2's red-quality line, and nothing recommended.
 * A count outcome keeps R27's rules on the same marginal ranges, worded as the next unit's cost (1 ÷ mROAS, R14), where
 * lower is better and an unbounded range reads "มากกว่า ฿X"; its gain is in units.
 *
 * @module components/budget/MoveAdvice
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BoxMessage } from '@/components/looker/app/appBoxShared';
import { OverviewCard } from '@/components/overview/OverviewBox';
import type { AdviceItem } from '@/services/budget/advice';
import type { OutcomeMetric } from '@/services/budget/budgetContract';
import { costRange, costRangeText, costText, isCount, unitName, unitsName } from '@/services/budget/outcomeUnits';
import { MIN_MOVE } from '@/services/budget/simulator';
import { fmtShortDate, type TFn } from '@/services/channels/channelFormat';
import { addDays } from '@/services/looker/lookerDateRange';
import { formatDelta, TONE_CLASS } from '@/services/looker/lookerDelta';
import { fmtCount, fmtMoney } from '@/services/overview/overviewFormat';
import { platformShortLabel } from '@/utils/platformLabel';
import { down2 } from './ContributionTable';

type Move = Extract<AdviceItem, { kind: 'move' }>;
type NotYet = Extract<AdviceItem, { kind: 'notYet' }>;
type Fmt = (n: number) => string;

const names = (channels: readonly string[]): string => channels.map((c) => platformShortLabel(c)).join(', ');

/** "+" only on a gain: fmtMoney and fmtCount already print a loss as "฿-1.50" and "-1.5". */
const signed = (n: number, fmt: Fmt): string => `${n > 0 ? '+' : ''}${fmt(n)}`;

/** The pair whose ranges part: by mROAS on revenue, by each side's next-unit cost on a count. */
const pairText = (m: Move, into: boolean, metric: OutcomeMetric, money: Fmt, t: TFn): string => {
  const sides = { amount: money(m.amount), from: platformShortLabel(m.from), to: platformShortLabel(m.to) };
  if (!isCount(metric)) {
    const pair = { ...sides, fromMroas: down2(m.fromMarginal.mean), toMroas: down2(m.toMarginal.mean) };
    return into ? t('budget.advice.move', pair) : t('budget.advice.cut', pair);
  }
  const pair = {
    ...sides, unit: unitName(metric, t),
    fromCost: costText(costRange(1, m.fromMarginal), money, t), toCost: costText(costRange(1, m.toMarginal), money, t),
  };
  return into ? t('budget.advice.moveCount', pair) : t('budget.advice.cutCount', pair);
};

/** The whole auto reallocation's gain, with its range and % of today: money on revenue, units on a count. */
const gainText = (m: Move, metric: OutcomeMetric, money: Fmt, t: TFn): string => {
  // Box 6's Δ figure, so pressing จัดงบอัตโนมัติ there shows the same percentage.
  const pct = formatDelta(m.gainPct / 100);
  if (!isCount(metric)) {
    return t('budget.advice.gain', { mean: signed(m.gain.mean, money), lo: money(m.gain.lo), hi: money(m.gain.hi), pct });
  }
  return t('budget.advice.gainCount', {
    units: unitsName(metric, t), mean: signed(m.gain.mean, fmtCount), lo: fmtCount(m.gain.lo), hi: fmtCount(m.gain.hi), pct,
  });
};

/** Ruling (a): the prototype's sentence, "ไป <to>" only where auto really gives `to` budget, then auto's destinations. */
const moveText = (m: Move, metric: OutcomeMetric, currency: string | null, t: TFn): string => {
  const money = (n: number): string => fmtMoney(n, currency);
  const ups = m.shifts.filter((s) => s.delta >= MIN_MOVE);
  const targets = ups.map((s) => `${platformShortLabel(s.channel)} ${signed(s.delta, money)}`).join(', ');
  return [
    pairText(m, ups.some((s) => s.channel === m.to), metric, money, t),
    ...(ups.length > 0 ? [t('budget.advice.targets', { targets })] : []),
    gainText(m, metric, money, t),
  ].join(' · ');
};

/** notYet's range: mROAS on revenue; on a count, the next unit's cost, 1 ÷ the top to 1 ÷ the bottom. */
const notYetText = (item: NotYet, metric: OutcomeMetric, currency: string | null, t: TFn): string => {
  const channel = platformShortLabel(item.channel);
  const others = names(item.overlaps);
  if (!isCount(metric)) {
    return t('budget.advice.notYet', { channel, lo: down2(item.marginal.lo), hi: down2(item.marginal.hi), others });
  }
  const range = costRangeText(costRange(1, item.marginal), (n) => fmtMoney(n, currency), t);
  return t('budget.advice.notYetCount', { channel, unit: unitName(metric, t), range, others });
};

const Lead: React.FC<{ text: string; tone: keyof typeof TONE_CLASS }> = ({ text, tone }) => (
  <><b className={TONE_CLASS[tone]}>{text}</b>{' '}</>
);

const Line: React.FC<{ item: AdviceItem; currency: string | null; metric: OutcomeMetric }> = ({ item, currency, metric }) => {
  const { t } = useTranslation();
  switch (item.kind) {
    case 'move':
      return <><Lead text={t('budget.advice.recommend')} tone="good" />{moveText(item, metric, currency, t)}</>;
    case 'notYet':
      return <><Lead text={t('budget.advice.notYetLead')} tone="neutral" />{notYetText(item, metric, currency, t)}</>;
    case 'stopped':
      return (
        <>
          <Lead text={t('budget.advice.stoppedLead')} tone="neutral" />
          {t('budget.advice.stopped', { channel: platformShortLabel(item.channel), date: fmtShortDate(addDays(item.lastActiveWeek, 6), t) })}
        </>
      );
    case 'quality':
      return <>{t('budget.status.dontDecide')}</>;
  }
};

/** adviceOf gives at most one move or quality item, and one notYet or stopped item per channel. */
const keyOf = (a: AdviceItem): string => (a.kind === 'notYet' || a.kind === 'stopped' ? `${a.kind}:${a.channel}` : a.kind);

interface Props {
  /** adviceOf(bundle), built by the page. */
  items: readonly AdviceItem[];
  /** The version's currency (bundle.currency). */
  currency: string | null;
  /** The version's outcome (bundle.outcome_metric): a count is worded in cost per unit. */
  metric: OutcomeMetric;
}

export const MoveAdvice: React.FC<Props> = ({ items, currency, metric }) => {
  const { t } = useTranslation();
  return (
    <OverviewCard id="budget-7" title={t('budget.advice.title')}>
      <p className="-mt-2 mb-3 text-xs text-neutral-700">{t('budget.advice.subtitle')}</p>
      {items.length === 0
        ? <BoxMessage text={t('budget.advice.none')} />
        : (
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-neutral-900">
            {items.map((a) => (
              <li key={keyOf(a)} data-advice={a.kind} className={a.kind === 'quality' ? `font-semibold ${TONE_CLASS.bad}` : undefined}>
                <Line item={a} currency={currency} metric={metric} />
              </li>
            ))}
          </ul>
        )}
    </OverviewCard>
  );
};
